import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { type ColumnDef } from '@tanstack/react-table';
import {
  APP_COLORS,
  getBounceActionBadgeClass,
  getBounceCodeBadgeClass,
  getBounceReasonBadgeClass,
  getBounceTypeBadgeClass,
  getStatusBadgeClass,
} from '../colors';
import Dropdown from '../common/Dropdown';
import Spinner from '../common/Spinner';
import { formatBackendDateTimeToLocal } from '../lib/dateTime';
import {
  type InvoiceNotificationDispatch,
  BounceEmailRecord,
  ConsolidatedRecord,
  Invoice,
  User,
} from '../types';

const NOTIFICATION_DISPATCH_LABELS: Record<InvoiceNotificationDispatch, string> = {
  EMAIL_ONLY: 'Email only',
  SMS_ONLY: 'SMS only',
  BOTH: 'Both',
};

const NOTIFICATION_DISPATCH_BADGE_CLASS: Record<InvoiceNotificationDispatch, string> = {
  EMAIL_ONLY: 'border-blue-200 bg-blue-50 text-blue-700',
  SMS_ONLY: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  BOTH: 'border-violet-200 bg-violet-50 text-violet-700',
};

type InvoiceColumnsOptions = {
  sendingInvoiceId: number | null;
  isSendInvoicePending: boolean;
  onOpenDescription: (description: string) => void;
  onSendInvoice: (invoiceId: number) => void;
  onEditInvoice: (invoice: Invoice) => void;
};

type UserColumnsOptions = {
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
};

export function createUserColumns(options: UserColumnsOptions): ColumnDef<User>[] {
  const { onEditUser, onDeleteUser } = options;

  return [
    {
      accessorKey: 'authorizeNetCustomerId',
      header: 'Authorize.Net ID',
      cell: ({ row }) => <span className={`font-medium ${APP_COLORS.indigoValueText}`}>{row.original.authorizeNetCustomerId}</span>,
    },
    {
      id: 'customerName',
      accessorFn: (user) => `${user.firstName} ${user.lastName}`.trim(),
      header: 'Name',
      cell: ({ row }) => <span>{`${row.original.firstName} ${row.original.lastName}`.trim()}</span>,
    },
    { accessorKey: 'email', header: 'Email' },
    {
      accessorKey: 'phoneNumber',
      header: 'Phone Number',
      cell: ({ row }) => <span className={`font-medium ${APP_COLORS.indigoValueText}`}>{row.original.phoneNumber}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <Dropdown
          placement="bottom-end"
          role="menu"
          usePortal={true}
          menuClassName="bg-white shadow-lg ring-1 ring-slate-200"
          trigger={(open: boolean, api: { toggle: () => void }) => (
            <button
              type="button"
              onClick={api.toggle}
              aria-label={`User ${row.original.id} actions`}
              aria-expanded={open}
              className={`rounded-md border bg-white p-1.5 text-slate-600 transition duration-150 ${
                open
                  ? 'border-indigo-300 text-indigo-700 ring-2 ring-indigo-100'
                  : 'border-slate-300 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700'
              }`}
            >
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </button>
          )}
          minWidth="min-w-[130px]"
        >
          <Dropdown.Item onClick={() => onEditUser(row.original)} className="text-slate-700 hover:bg-indigo-50">
            Edit
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onDeleteUser(row.original)} className="text-red-700 hover:bg-red-50">
            Delete
          </Dropdown.Item>
        </Dropdown>
      ),
    },
  ];
}

export function createInvoiceColumns(options: InvoiceColumnsOptions): ColumnDef<Invoice>[] {
  const { sendingInvoiceId, isSendInvoicePending, onOpenDescription, onSendInvoice, onEditInvoice } = options;

  return [
    {
      accessorKey: 'invoiceId',
      header: 'Invoice ID',
      cell: ({ row }) => <span className={`font-medium ${APP_COLORS.indigoValueText}`}>{row.original.invoiceId}</span>,
    },
    { accessorKey: 'customerName', header: 'Name' },
    { accessorKey: 'customerEmail', header: 'Email' },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => <span>${row.original.amount}</span>,
    },
    {
      accessorKey: 'notificationDispatch',
      header: 'Notifications',
      cell: ({ row }) => {
        const value = row.original.notificationDispatch;
        if (!value) {
          return <span className="text-slate-400">—</span>;
        }
        return (
          <span
            className={`rounded-md border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${NOTIFICATION_DISPATCH_BADGE_CLASS[value]}`}
          >
            {NOTIFICATION_DISPATCH_LABELS[value]}
          </span>
        );
      },
    },
    {
      id: 'description',
      header: 'Description',
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => onOpenDescription(row.original.description)}
          className={`rounded-md px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 ${APP_COLORS.indigoSoftBackground}`}
        >
          View
        </button>
      ),
    },
    // { accessorKey: 'serviceDate', header: () => <span title="Date Of Service">DOS</span> },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getStatusBadgeClass(row.original.status)}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => formatBackendDateTimeToLocal(row.original.createdAt),
    },
    {
      accessorKey: 'isOpen',
      header: 'Opened',
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
            row.original.isOpen ? APP_COLORS.openedYesBadge : APP_COLORS.openedNoBadge
          }`}
        >
          {row.original.isOpen ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      accessorKey: 'firstOpenedAt',
      header: 'First Opened At',
      cell: ({ row }) => <span>{formatBackendDateTimeToLocal(row.original.firstOpenedAt)}</span>,
    },
    {
      accessorKey: 'lastOpenedAt',
      header: 'Last Opened At',
      cell: ({ row }) => <span>{formatBackendDateTimeToLocal(row.original.lastOpenedAt)}</span>,
    },
    { accessorKey: 'clickCount', header: 'Click Count' },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const isDraft = row.original.status.toUpperCase() === 'DRAFT';
        if (!isDraft) {
          return <span className="text-xs text-slate-400">-</span>;
        }

        const isSending = isSendInvoicePending && sendingInvoiceId === row.original.invoiceId;
        return (
          <Dropdown
            placement="bottom-end"
            role="menu"
            usePortal={true}
            menuClassName="bg-white shadow-lg ring-1 ring-slate-200"
            trigger={(open: boolean, api: { toggle: () => void }) => (
              <button
                type="button"
                onClick={api.toggle}
                aria-label={`Invoice ${row.original.invoiceId} actions`}
                aria-expanded={open}
                className={`rounded-md border bg-white p-1.5 text-slate-600 transition duration-150 ${
                  open
                    ? 'border-indigo-300 text-indigo-700 ring-2 ring-indigo-100'
                    : 'border-slate-300 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700'
                }`}
              >
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </button>
            )}
            minWidth="min-w-[130px]"
          >
            <Dropdown.Item
              onClick={() => onSendInvoice(row.original.invoiceId)}
              className={isSending ? 'pointer-events-none text-slate-400 hover:bg-white' : 'text-slate-700 hover:bg-indigo-50'}
            >
              <span className="flex items-center gap-2">
                {isSending && <Spinner size="sm" />}
                <span>{isSending ? 'Sending...' : 'Send'}</span>
              </span>
            </Dropdown.Item>
            <Dropdown.Item onClick={() => onEditInvoice(row.original)} className="text-slate-700 hover:bg-indigo-50">
              Edit
            </Dropdown.Item>
          </Dropdown>
        );
      },
    },
  ];
}

export function createConsolidatedColumns(): ColumnDef<ConsolidatedRecord>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      enableSorting: true,
      cell: ({ row }) => <span>{row.original.firstName} {row.original.lastName}</span>,
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Phone Number',
      enableSorting: true,
      cell: ({ row }) => <span className={`font-medium ${APP_COLORS.indigoValueText}`}>{row.original.phoneNumber}</span>,
    },
    { accessorKey: 'email', header: 'Email', enableSorting: true },
    {
      accessorKey: 'invoiceId',
      enableSorting: true,
      header: 'Invoice ID',
      cell: ({ row }) => <span className={`font-medium ${APP_COLORS.indigoValueText}`}>{row.original.invoiceId}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Invoice Status',
      enableSorting: false,
      cell: ({ row }) => (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getStatusBadgeClass(row.original.status)}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      accessorKey: 'transactionId',
      header: 'Transaction ID',
      enableSorting: true,
      cell: ({ row }) => <span className={`font-medium ${APP_COLORS.indigoValueText}`}>{row.original.transactionId}</span>,
    },
    {
      accessorKey: 'gatewayStatus',
      header: 'Transaction Status',
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getStatusBadgeClass(
            row.original.transactionStatus
          )}`}
        >
          {row.original.transactionStatus}
        </span>
      ),
    },
   
  ];
}

export function createBounceEmailColumns(): ColumnDef<BounceEmailRecord>[] {
  return [
    { accessorKey: 'finalRecipient', header: 'Email' },
    { accessorKey: 'subject', header: 'Subject' },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ row }) => (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getBounceActionBadgeClass(row.original.action)}`}>
          {row.original.action}
        </span>
      ),
    },
    {
      accessorKey: 'bounceType',
      header: 'Bounce Type',
      cell: ({ row }) => (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getBounceTypeBadgeClass(row.original.bounceType)}`}>
          {row.original.bounceType}
        </span>
      ),
    },
    {
      accessorKey: 'bounceCode',
      header: 'Bounce Code',
      cell: ({ row }) => (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getBounceCodeBadgeClass(row.original.bounceCode)}`}>
          {row.original.bounceCode}
        </span>
      ),
    },
    {
      accessorKey: 'bounceReason',
      header: 'Bounce Reason',
      cell: ({ row }) => (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${getBounceReasonBadgeClass(row.original.bounceReason)}`}>
          {row.original.bounceReason}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => formatBackendDateTimeToLocal(row.original.createdAt),
    },
    {
      accessorKey: 'bouncedAt',
      header: 'Bounced At',
      cell: ({ row }) => formatBackendDateTimeToLocal(row.original.bouncedAt),
    },
  ];
}
